import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

/**
 * Simple math captcha. Renders a server-issued challenge + input, exposes
 * `.getValues()` and `.reset()` via ref. Parent forms are expected to include
 * `captcha_token` and `captcha_answer` in the request payload.
 *
 * Usage:
 *   const captchaRef = useRef();
 *   ...
 *   <Captcha ref={captchaRef} />
 *   ...
 *   const { captcha_token, captcha_answer } = captchaRef.current.getValues();
 *   await api.post("/feedback", { ...form, captcha_token, captcha_answer });
 *   captchaRef.current.reset(); // fetch a fresh challenge after submit
 */
const Captcha = forwardRef(function Captcha({ className = "", testId = "captcha" }, ref) {
  const [challenge, setChallenge] = useState({ token: "", question: "" });
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/captcha");
      setChallenge(r.data || {});
      setAnswer("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useImperativeHandle(ref, () => ({
    getValues: () => ({ captcha_token: challenge.token, captcha_answer: answer }),
    reset: load,
    isReady: () => !!(challenge.token && answer.trim()),
  }));

  return (
    <div className={`rounded-lg border border-slate-200 bg-slate-50 p-3 ${className}`} data-testid={testId}>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-slate-500">
        Human check
      </label>
      <div className="flex items-center gap-2">
        <div className="grid h-10 min-w-24 flex-shrink-0 select-none place-items-center rounded-md border border-slate-300 bg-white px-4 font-heading text-base font-bold tracking-wider text-slate-800" data-testid={`${testId}-question`}>
          {loading ? "…" : challenge.question || "?"}
        </div>
        <span className="text-slate-500">=</span>
        <Input
          type="text"
          inputMode="numeric"
          placeholder="Answer"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          className="h-10 w-28"
          data-testid={`${testId}-input`}
          autoComplete="off"
          required
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={load}
          disabled={loading}
          className="h-9 px-2"
          aria-label="Refresh captcha"
          data-testid={`${testId}-refresh`}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>
    </div>
  );
});

export default Captcha;
